import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { SESSION_EXPIRED_EVENT, tokenStore } from '@/lib/apiClient';
import type { User } from '@/types';
import * as authApi from './api';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore the session on load if we still hold tokens.
  useEffect(() => {
    let active = true;
    void (async () => {
      if (tokenStore.access || tokenStore.refresh) {
        try {
          const me = await authApi.fetchMe();
          if (active) setUser(me);
        } catch {
          tokenStore.clear();
        }
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  // React to unrecoverable auth failures raised by the API client.
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      setUser,
      login: async (username, password) => {
        const auth = await authApi.login(username, password);
        setUser(auth.user);
      },
      logout: async () => {
        await authApi.logout();
        setUser(null);
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/** Administrador or Encarregado — the "acessos de administrador" the client asked for. */
// eslint-disable-next-line react-refresh/only-export-components
export function useVeEquipa(): boolean {
  const { user } = useAuth();
  return user?.role === 'Administrador' || user?.role === 'Encarregado';
}

// eslint-disable-next-line react-refresh/only-export-components
export function useIsAdmin(): boolean {
  const { user } = useAuth();
  return user?.role === 'Administrador';
}
